import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { publicApi } from '../services/api';
import { Link } from 'react-router-dom';
import { Database, Loader, ArrowLeft } from 'lucide-react';

export const BrowseDataPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await publicApi.browse();
        setRows(response.data.data || []);
      } catch (e) {
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, search]);

  const columns = useMemo(() => {
    const first = filteredRows[0] || rows[0];
    return first ? Object.keys(first) : [];
  }, [filteredRows, rows]);

  return (
    <div className="min-h-screen app-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="glass p-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="section-title flex items-center gap-2">
                <Database className="w-6 h-6" />
                Browse Data
              </h1>
              <p className="text-gray-400">View scouting data in the browser (no downloads)</p>
            </div>
            <Link to="/login" className="btn btn-secondary flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </motion.div>

        <div className="glass p-6 mb-4">
          <label className="form-label">Search</label>
          <input
            className="form-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type to filter rows..."
          />
          <p className="text-xs text-gray-500 mt-2">
            Showing <span className="text-gray-300 font-medium">{filteredRows.length}</span> row(s)
          </p>
        </div>

        {error && (
          <div className="glass p-4 mb-4 bg-red-500/10 border border-red-500/30 text-red-300">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="glass p-12 flex justify-center">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="glass p-6 overflow-auto">
            {filteredRows.length === 0 ? (
              <p className="text-gray-400">No data found.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-300">
                    {columns.map((c) => (
                      <th key={c} className="py-2 pr-4 whitespace-nowrap">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-gray-200">
                  {filteredRows.slice(0, 500).map((r, idx) => (
                    <tr key={idx} className="border-t border-gray-700/50">
                      {columns.map((c) => (
                        <td key={c} className="py-2 pr-4 align-top whitespace-nowrap">
                          {String(r[c] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {filteredRows.length > 500 && (
              <p className="text-xs text-gray-500 mt-3">
                Displaying first 500 rows (refine search to narrow it down).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

